








// Import vendors ----------------------------------------------------------------------------------
import { defineComponent, computed, PropType } from '@vue/composition-api';
// Import components -------------------------------------------------------------------------------
import ChartEvolutionContainer from '@/components/charts/ChartEvolutionContainer.vue';
// Import plugins ----------------------------------------------------------------------------------
import i18n from '@/plugins/i18n';
// Import utils - ----------------------------------------------------------------------------------
import { useChart, EThemeColor } from '@/utils/charts.utils';
import { EUnitKey } from '@/utils/parameter-units.utils';
// Import types ------------------------------------------------------------------------------------
import type { TDataStandard } from '@/components/patient/PatientEvolution.vue';
// -------------------------------------------------------------------------------------------------

export default defineComponent({
  name: 'ChartEvolutionFlatFoot',
  components: {
    ChartEvolutionContainer
  },
  props: {
    manifest: {
      type: Array,
      required: true
    },
    analysisType: {
      type: String as PropType<'walk' | 'run'>,
      default: 'walk'
    },
    flatFoot: {
      type: Object
    },
    norms: {
      type: Object
    }
  },
  setup(properties) {
    const {
      evolutionChartStructure,
      getSideData,
      getSideDataVariations,
      getSerieItemStructure,
      getNormsStructure
    } = useChart();

    const title = computed(() => `commons.standards.${properties.analysisType}.flat-foot`);

    const flatFoot: TDataStandard = computed(() => getSideData(properties.flatFoot));

    const dataVariations = computed(() =>
      getSideDataVariations({ left: flatFoot.value?.left.values, right: flatFoot.value?.right.values })
    );

    const valid = computed(() => !!flatFoot.value);

    const series = computed(() => {
      let data = [
        // Left
        getSerieItemStructure(
          'flat_foot_left',
          `${i18n.t(`commons.standards.${properties.analysisType}.flat-foot`)} (${(
            i18n.t('commons.standards.left') as string
          ).slice(0, 1)})`,
          flatFoot.value?.left.values,
          EThemeColor.Chart1
        ),
        getSerieItemStructure(
          'flat_foot_left',
          undefined,
          flatFoot.value?.left.ranges,
          EThemeColor.Chart1,
          true
        ),
        // Right
        getSerieItemStructure(
          'flat_foot_right',
          `${i18n.t(`commons.standards.${properties.analysisType}.flat-foot`)} (${(
            i18n.t('commons.standards.right') as string
          ).slice(0, 1)})`,
          flatFoot.value?.right.values,
          EThemeColor.Chart2
        ),
        getSerieItemStructure(
          'flat_foot_right',
          undefined,
          flatFoot.value?.right.ranges,
          EThemeColor.Chart2,
          true
        )
      ];
      // Have norms
      if (properties.norms)
        data.push(getNormsStructure('flat_foot_norms', properties.norms as { min: number[]; max: number[] }));

      return data;
    });

    const chartOptions = computed(() => {
      return {
        ...evolutionChartStructure(title, properties.manifest, EUnitKey.Degree, 1),
        series: series.value
      };
    });

    return {
      title,
      dataVariations,
      chartOptions,
      valid
    };
  }
});
